import LoadingScreen from "../../reusables/LoadingScreen";
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { getToken } from "../../auth/authConfig";
import axios from "axios";
import AddStandardModal from "./AddStandardModal";
import AddRequirementModal from "./AddRequirementModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import StandardHeader from "./StandardHeader";
import StandardStatsAndSearch from "./StandardStatsAndSearch";
import StandardCardList from "./StandardCardList";
import Error403 from "../../reusables/Error403";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import Login from "../../auth/Login";

function StandardsManagement() {
  const [standards, setStandards] = useState({ data: [] });
  const [showAddStandardModal, setShowAddStandardModal] = useState(false);
  const [showAddRequirementModal, setShowAddRequirementModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStandard, setSelectedStandard] = useState("");
  const [newStandardName, setNewStandardName] = useState("");
  const [standardToDelete, setStandardToDelete] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedStandards, setExpandedStandards] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [tags, setTags] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [accessPage, setAccessPage] = useState(true);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim() !== "") {
      e.preventDefault();
      const newTag = inputValue.trim();
      if (!tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setInputValue("");
    }
  };

  const addTag = () => {
    if (inputValue.trim() !== "") {
      const newTag = inputValue.trim();
      if (!tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setInputValue("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const toggleStandardExpansion = (standardName) => {
    setExpandedStandards((prev) => ({
      ...prev,
      [standardName]: !prev[standardName],
    }));
  };

  const getStandardsAndRequirements = async () => {
    try {
      const token = await getToken();
      const url = `${process.env.REACT_APP_SERVER_URL}/super/getStandardsAndRequirements`;
      setPageLoading(true);
      const response = await axios.get(url, {
        headers: {
          "oidc-token": token,
        },
      });

      if (response.status === 200) {
        setStandards(response.data);
      } else {
        console.error("Unexpected response:", response);
      }
    } catch (error) {
      console.error("Error fetching standards:", error);
      if (error.response && error.response.status === 403) {
        setAccessPage(false);
      }
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    getStandardsAndRequirements();
  }, []);

  const handleAddStandard = async () => {
    if (!newStandardName.trim()) return;
    setLoading(true);
    try {
      const token = await getToken();
      const url = `${process.env.REACT_APP_SERVER_URL}/super/addStandard`;
      const response = await axios.post(
        url,
        { standard: newStandardName },
        { headers: { "oidc-token": token } }
      );
      if (response.status === 200 || response.status === 201) {
        setNewStandardName("");
        setShowAddStandardModal(false);
        getStandardsAndRequirements();
      }
    } catch (error) {
      console.error("Error adding standard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRequirement = async () => {
    if (!tags.length || !selectedStandard) return;
    setLoading(true);
    try {
      const token = await getToken();
      const url = `${process.env.REACT_APP_SERVER_URL}/super/addRequirement`;
      const response = await axios.post(
        url,
        { standard: selectedStandard, requirement: tags },
        { headers: { "oidc-token": token } }
      );
      if (response.status === 200 || response.status === 201) {
        setTags([]);
        setSelectedStandard("");
        setShowAddRequirementModal(false);
        getStandardsAndRequirements();
      }
    } catch (error) {
      console.error("Error adding requirement:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStandard = async () => {
    if (!standardToDelete) return;
    setLoading(true);
    try {
      const token = await getToken();
      const url = `${process.env.REACT_APP_SERVER_URL}/super/deleteStandard`;
      const response = await axios.post(
        url,
        { standard: standardToDelete },
        { headers: { "oidc-token": token } }
      );
      if (response.status === 200) {
        setShowDeleteModal(false);
        setStandardToDelete("");
        getStandardsAndRequirements();
      }
    } catch (error) {
      console.error("Error deleting standard:", error);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (standardName) => {
    setStandardToDelete(standardName);
    setShowDeleteModal(true);
  };

  const openAddRequirementModal = (standardName) => {
    setSelectedStandard(standardName);
    setShowAddRequirementModal(true);
  };

  const filteredStandards =
    standards?.data?.filter(
      (standard) =>
        standard.standard.toLowerCase().includes(searchTerm.toLowerCase()) ||
        standard.requirements.some((req) =>
          req.toLowerCase().includes(searchTerm.toLowerCase())
        )
    ) || [];

  const offset = currentPage * itemsPerPage;
  const paginatedStandards = filteredStandards.slice(
    offset,
    offset + itemsPerPage
  );
  const pageCount = Math.ceil(filteredStandards.length / itemsPerPage);

  if (pageLoading) {
    return (
      <div style={{ height: "100vh" }}>
        <LoadingScreen />
      </div>
    );
  }

  return (
    <>
      <AuthenticatedTemplate>
        {accessPage && (
          <Box sx={{ minHeight: "100vh", zoom: ".8" }}>
            <StandardHeader onAddClick={() => setShowAddStandardModal(true)} />

            <div className="p-6">
              <StandardStatsAndSearch
                standards={standards}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                pageCount={pageCount}
                handlePageChange={handlePageChange}
              />

              <StandardCardList
                filteredStandards={paginatedStandards}
                expandedStandards={expandedStandards}
                toggleStandardExpansion={toggleStandardExpansion}
                openAddRequirementModal={openAddRequirementModal}
                openDeleteModal={openDeleteModal}
                searchTerm={searchTerm}
                setShowAddStandardModal={setShowAddStandardModal}
              />
            </div>

            <AddStandardModal
              open={showAddStandardModal}
              onClose={() => setShowAddStandardModal(false)}
              newStandardName={newStandardName}
              setNewStandardName={setNewStandardName}
              handleAddStandard={handleAddStandard}
              loading={loading}
            />

            <AddRequirementModal
              open={showAddRequirementModal}
              onClose={() => setShowAddRequirementModal(false)}
              selectedStandard={selectedStandard}
              tags={tags}
              handleRemoveTag={handleRemoveTag}
              inputValue={inputValue}
              setInputValue={setInputValue}
              handleKeyDown={handleKeyDown}
              addTag={addTag}
              handleAddRequirement={handleAddRequirement}
              loading={loading}
            />

            <DeleteConfirmationModal
              open={showDeleteModal}
              onClose={() => setShowDeleteModal(false)}
              standardToDelete={standardToDelete}
              handleDeleteStandard={handleDeleteStandard}
              loading={loading}
            />
          </Box>
        )}

        {!accessPage && (
          <div className="flex items-center justify-center h-screen">
            <Error403 />
          </div>
        )}
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>


    </>
  );
}

export default StandardsManagement;
